import React, { memo, useCallback, useContext, useMemo, useState } from 'react'
import { MenuActionItem } from 'src/interfaces/ui'
import { ActionMenu } from 'src/components/shared/ActionMenu'
import { CancelSmallMinor, TickMinor } from '@shopify/polaris-icons'
import tw from 'twin.macro'
import { useDispatch } from 'react-redux'
import { showToast } from 'src/utils/toast'
import { handleApiError } from 'src/utils/errorHandlers'
import { SensitiveActionWithPictureModal } from 'src/components/shared/SubmitActionWithPictureModal'
import NotFoundImage from 'src/assets/images/not-found.png'
import { ReloadWrapperContext } from 'src/components/wrapper/reload-wrapper/ReloadWrapper.context'
import { SubcontractorRequestsTableContext } from './SubcontractorRequestsTable.context'
import { DeclineSubcontractorRequests } from 'src/constants/actionTypes'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { ApproveSubcontractorRequestsModal } from '../approve-subcontractor-requests/ApproveSubcontractorRequestsModal'

const SubcontractorRequestsActionMenuComponent = () => {
  const dispatch = useDispatch()

  const { reload } = useContext(ReloadWrapperContext)

  const { subcontractorRequestToAction, actionAnchor, closeSubcontractorRequestItemAction } =
    useContext(SubcontractorRequestsTableContext)

  const isOpen = Boolean(actionAnchor)

  const [isApproveContractorsOpen, setIsApproveContractorsOpen] = useState(false)
  const [isConfirmDeclineOpen, setIsConfirmDeclineOpen] = useState(false)

  const handleOpenConfirmDecline = useCallback(() => {
    setIsConfirmDeclineOpen(true)
  }, [])

  const handleCloseConfirmDecline = useCallback(() => {
    setIsConfirmDeclineOpen(false)
  }, [])

  const handleOpenApproveContractors = useCallback(() => {
    setIsApproveContractorsOpen(true)
  }, [])

  const handleCloseApproveContractors = useCallback(() => {
    setIsApproveContractorsOpen(false)
  }, [])

  const handleDeclineContractor = useCallback(async () => {
    if (!subcontractorRequestToAction) {
      return
    }

    try {
      await createAsyncAction(
        dispatch,
        DeclineSubcontractorRequests.request({
          selectedSubcontractorRequestIds: [subcontractorRequestToAction.id],
        }),
      )

      reload()

      showToast('Contractor declined. Ask the master contractor to link him again if needed.')
    } catch (err) {
      handleApiError(err)
    }

    handleCloseConfirmDecline()
  }, [subcontractorRequestToAction, reload])

  const actionItems: MenuActionItem[] = useMemo(() => {
    const res: MenuActionItem[] = []

    res.push({
      label: 'Approve',
      icon: <TickMinor width={20} height={20} />,
      onClick: () => {
        if (!subcontractorRequestToAction) {
          return
        }

        handleOpenApproveContractors()
        closeSubcontractorRequestItemAction()
      },
    })

    res.push({
      label: 'Decline',
      icon: <CancelSmallMinor width={20} height={20} />,
      onClick: () => {
        if (!subcontractorRequestToAction) {
          return
        }

        handleOpenConfirmDecline()
        closeSubcontractorRequestItemAction()
      },
    })

    return res
  }, [subcontractorRequestToAction, reload])

  const subcontractorRequestId = useMemo(
    () => (subcontractorRequestToAction?.id ? [subcontractorRequestToAction.id] : []),
    [subcontractorRequestToAction],
  )

  return (
    <>
      <ActionMenu
        anchorEl={actionAnchor}
        itemClassName={tw`min-w-[208px]`}
        isOpen={isOpen}
        items={actionItems}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClose={closeSubcontractorRequestItemAction}
      />
      <SensitiveActionWithPictureModal
        isOpen={isConfirmDeclineOpen}
        title="Are you sure you want to decline this contractor?"
        text="Declining a contractor will remove them from the table. If you want to approve this contractor later, ask the master contractor to link them to you again."
        submitLabel="Decline"
        onClose={handleCloseConfirmDecline}
        onSubmit={handleDeclineContractor}
        imageSrc={NotFoundImage}
      />
      <ApproveSubcontractorRequestsModal
        isOpen={isApproveContractorsOpen}
        onClose={handleCloseApproveContractors}
        onSubmit={handleCloseApproveContractors}
        subcontractorRequestsSelection={subcontractorRequestId}
      />
    </>
  )
}

export const SubcontractorRequestsActionMenu = memo(SubcontractorRequestsActionMenuComponent)
